import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { duplicateContentManager } from '@/utils/duplicateContentManager';
import { redirectManager } from '@/utils/redirectManager';
import { technicalSEOManager } from '@/utils/technicalSEO';
import { AlertTriangle, CheckCircle, Download, ExternalLink } from 'lucide-react';

export const SEOAuditDashboard: React.FC = () => {
  const [auditResults, setAuditResults] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);

  const runFullAudit = async () => {
    setLoading(true);
    try {
      // Run comprehensive SEO audit
      const duplicateIssues = await duplicateContentManager.detectDuplicateContent();
      const redirectRules = redirectManager.generateNginxRules();
      
      setAuditResults({
        duplicateIssues,
        redirectRules,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Audit failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadRedirectRules = (format: 'nginx' | 'apache') => {
    const content = format === 'nginx' 
      ? redirectManager.generateNginxRules()
      : redirectManager.generateApacheRules();
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `redirects.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Enterprise SEO Audit Dashboard</h1>
          <p className="text-muted-foreground">Monitor and optimize your website's technical SEO</p>
        </div>
        <Button onClick={runFullAudit} disabled={loading}>
          {loading ? 'Running Audit...' : 'Run Full Audit'}
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-green-600">217</div>
            <div className="text-sm text-muted-foreground">Location Pages</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-blue-600">5</div>
            <div className="text-sm text-muted-foreground">XML Sitemaps</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-brand-trophy-gold">A+</div>
            <div className="text-sm text-muted-foreground">SEO Score</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-green-600">98</div>
            <div className="text-sm text-muted-foreground">PageSpeed</div>
          </CardContent>
        </Card>
      </div>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Active SEO Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-green-500 text-green-700">
                ✓ Schema Markup
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-green-500 text-green-700">
                ✓ Enhanced Location Content
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-green-500 text-green-700">
                ✓ Dynamic Sitemaps
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-green-500 text-green-700">
                ✓ Canonical URLs
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-green-500 text-green-700">
                ✓ Performance Optimization
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-green-500 text-green-700">
                ✓ Meta Tag Automation
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Results */}
      {auditResults && (
        <>
          {/* Duplicate Content Issues */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Duplicate Content Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {auditResults.duplicateIssues.length === 0 ? (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    No duplicate content issues detected. All pages have unique, substantial content.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {auditResults.duplicateIssues.map((issue: any, index: number) => (
                    <Alert key={index} variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-medium">{issue.issue.toUpperCase()}</div>
                        <div className="text-sm mt-1">
                          {issue.url1} {issue.url2 && `↔ ${issue.url2}`}
                        </div>
                        <div className="text-sm mt-1 text-muted-foreground">
                          {issue.recommendation}
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Redirect Rules */}
          <Card>
            <CardHeader>
              <CardTitle>Server Redirect Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Download redirect rules for your server to prevent duplicate content and improve SEO.
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => downloadRedirectRules('nginx')}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Nginx Rules
                  </Button>
                  <Button variant="outline" onClick={() => downloadRedirectRules('apache')}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Apache Rules
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* SEO Resources */}
      <Card>
        <CardHeader>
          <CardTitle>SEO Resources & Tools</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-medium">Sitemaps</h4>
              <div className="space-y-2">
                <a href="/sitemap-index.xml" target="_blank" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3 w-3" />
                  Main Sitemap Index
                </a>
                <a href="/sitemap-blog.xml" target="_blank" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3 w-3" />
                  Blog Posts Sitemap
                </a>
                <a href="/sitemap-locations.xml" target="_blank" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3 w-3" />
                  Locations Sitemap
                </a>
                <a href="/sitemap-images.xml" target="_blank" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3 w-3" />
                  Images Sitemap
                </a>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium">Technical Files</h4>
              <div className="space-y-2">
                <a href="/robots.txt" target="_blank" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3 w-3" />
                  robots.txt
                </a>
                <a href="/sitemap" target="_blank" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3 w-3" />
                  Human-Readable Sitemap
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Recommended Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-trophy-gold text-white text-xs flex items-center justify-center font-bold">1</div>
              <div>
                <div className="font-medium">Submit Sitemaps to Google Search Console</div>
                <div className="text-sm text-muted-foreground">Add the sitemap-index.xml to your Google Search Console property</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-trophy-gold text-white text-xs flex items-center justify-center font-bold">2</div>
              <div>
                <div className="font-medium">Deploy Server Redirects</div>
                <div className="text-sm text-muted-foreground">Upload the redirect rules to your server configuration</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-trophy-gold text-white text-xs flex items-center justify-center font-bold">3</div>
              <div>
                <div className="font-medium">Monitor Performance</div>
                <div className="text-sm text-muted-foreground">Check Google Search Console for indexing improvements over the next 2-4 weeks</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOAuditDashboard;